/**
 * lazyImageLoader.ts
 * Shared lazy URL → HTMLImageElement decode helper for the web
 * Canvas2D renderers in this folder.
 *
 * Each call site owns an {@link ImageSlot} — a mutable holder for the
 * decoded image plus a one-shot latch so the prefetch runs at most
 * once. {@link ensureImageLoaded} kicks the prefetch on first call and
 * populates `slot.current` when the texture is ready. A failed decode
 * leaves `slot.current` null so callers keep falling back to their
 * colour-rect paint instead of throwing inside the per-frame draw loop.
 *
 * @module lazyImageLoader
 */

/// <reference lib="dom" />

import { loadImage } from './imageLoader';

/** Mutable holder used by callers to hand a cache slot to the loader. */
export interface ImageSlot {
  /** Decoded image once the prefetch resolves, else `null`. */
  current: globalThis.HTMLImageElement | null;
  /** Latch — `true` once {@link ensureImageLoaded} has begun a fetch. */
  loadStarted: boolean;
}

/**
 * Create a fresh, empty cache slot.
 *
 * @returns A slot with `current = null` and `loadStarted = false`.
 */
export function createImageSlot(): ImageSlot {
  return { current: null, loadStarted: false };
}

/**
 * Trigger a one-shot lazy decode of `url` into `slot`. No-op once the
 * load has been attempted on this slot. A failed decode (broken URL,
 * zero-sized image) leaves the slot null and the caller's fallback
 * paint path runs every frame.
 *
 * @param slot - Cache slot to populate on decode.
 * @param url - Static asset URL produced by a Vite `import x from '...png'`.
 * @returns void — mutates `slot` as a side effect.
 */
export function ensureImageLoaded(slot: ImageSlot, url: string): void {
  if (slot.loadStarted) return;
  slot.loadStarted = true;
  void loadImage(url).then((result) => {
    if (result.ok && result.img.naturalWidth > 0) {
      slot.current = result.img;
    }
  });
}
